/**
 * useHelpAssistant — assistant d'aide IA du Help Drawer.
 *
 * Détient le fil de discussion (multi-tours léger) et consomme le flux SSE de
 * `POST /api/knowledge/assistant` via `fetch` + `ReadableStream` (EventSource
 * impossible car POST). Serveur sans état : on lui renvoie l'historique borné.
 */

import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE } from '@/lib/api'

export interface AssistantSource {
    slug: string
    title: string
    /** Article le plus pertinent (mis en avant dans la liste des sources). */
    primary?: boolean
    /** Titre de la SECTION qui a fait gagner l'article au retrieval (étape 4.2) :
     *  affichée dans la carte et cible du deep-link. `null` si non déterminée. */
    section?: string | null
}

export interface AssistantTurn {
    role: 'user' | 'assistant'
    content: string
    sources?: AssistantSource[]
}

/** Plafond de questions par conversation (au-delà → « Nouvelle question »). */
export const MAX_ASSISTANT_TURNS = 6
/** Nombre max de messages d'historique renvoyés au serveur (borne serveur = 10). */
const MAX_HISTORY = 10

interface SseEvent {
    event: string
    data: { text?: string; sources?: AssistantSource[]; code?: string; request_id?: string }
}

function parseSseBlock(raw: string): SseEvent | null {
    let event = 'message'
    let dataStr = ''
    for (const line of raw.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) dataStr += line.slice(5).trim()
    }
    if (!dataStr) return null
    try {
        return { event, data: JSON.parse(dataStr) }
    } catch {
        return null
    }
}

export interface UseHelpAssistantReturn {
    messages: AssistantTurn[]
    isStreaming: boolean
    error: string | null
    /** Vrai quand le plafond de tours est atteint (champ de relance désactivé). */
    atLimit: boolean
    /** Pose une question de SUIVI : empile sur la conversation en cours. */
    ask: (question: string) => void
    /** Démarre une NOUVELLE conversation : coupe le flux en cours, repart d'un fil vide. */
    startNew: (question: string) => void
    /** Restaure un fil stocké TEL QUEL (sans appel réseau) — historique conversations (§5). */
    restore: (turns: AssistantTurn[]) => void
    reset: () => void
}

export function useHelpAssistant(): UseHelpAssistantReturn {
    const { i18n } = useTranslation()
    const [messages, setMessages] = useState<AssistantTurn[]>([])
    const [isStreaming, setIsStreaming] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const abortRef = useRef<AbortController | null>(null)

    const reset = useCallback(() => {
        abortRef.current?.abort()
        abortRef.current = null
        setMessages([])
        setError(null)
        setIsStreaming(false)
    }, [])

    const run = useCallback(
        (question: string, fresh: boolean) => {
            const q = question.trim()
            if (!q || isStreaming) return

            const lang = i18n.language?.slice(0, 2) || 'fr'
            // Historique = messages déjà complets (borné), capturé AVANT les tours
            // optimistes. Vide pour une NOUVELLE conversation (ce n'est pas un suivi).
            const history = fresh
                ? []
                : messages.map((m) => ({ role: m.role, content: m.content })).slice(-MAX_HISTORY)

            // Nouvelle conversation : couper un éventuel flux en cours puis repartir
            // d'un fil vide ; sinon empiler sur le fil existant.
            if (fresh) abortRef.current?.abort()
            setError(null)
            setMessages((prev) => [
                ...(fresh ? [] : prev),
                { role: 'user', content: q },
                { role: 'assistant', content: '' },
            ])
            setIsStreaming(true)

            const controller = new AbortController()
            abortRef.current = controller

            const appendToken = (text: string) =>
                setMessages((prev) => {
                    const next = [...prev]
                    const last = next[next.length - 1]
                    if (last?.role === 'assistant') {
                        next[next.length - 1] = { ...last, content: last.content + text }
                    }
                    return next
                })

            const setSources = (sources: AssistantSource[]) =>
                setMessages((prev) => {
                    const next = [...prev]
                    const last = next[next.length - 1]
                    if (last?.role === 'assistant') {
                        next[next.length - 1] = { ...last, sources }
                    }
                    return next
                })

            void (async () => {
                try {
                    const res = await fetch(`${API_BASE}/knowledge/assistant`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ question: q, lang, history }),
                        signal: controller.signal,
                    })
                    if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`)

                    const reader = res.body.getReader()
                    const decoder = new TextDecoder()
                    let buffer = ''

                    for (;;) {
                        const { done, value } = await reader.read()
                        if (done) break
                        buffer += decoder.decode(value, { stream: true })

                        let sep: number
                        while ((sep = buffer.indexOf('\n\n')) !== -1) {
                            const block = buffer.slice(0, sep)
                            buffer = buffer.slice(sep + 2)
                            const evt = parseSseBlock(block)
                            if (!evt) continue
                            if (evt.event === 'token' && evt.data.text) appendToken(evt.data.text)
                            else if (evt.event === 'sources' && evt.data.sources)
                                setSources(evt.data.sources)
                            else if (evt.event === 'error') setError('error')
                        }
                    }
                } catch (err) {
                    if ((err as { name?: string })?.name !== 'AbortError') setError('error')
                } finally {
                    setIsStreaming(false)
                    abortRef.current = null
                }
            })()
        },
        [messages, isStreaming, i18n.language],
    )

    const ask = useCallback((question: string) => run(question, false), [run])
    const startNew = useCallback((question: string) => run(question, true), [run])

    // Restaure un fil archivé SANS appel réseau : coupe un éventuel flux, recolle
    // les tours stockés. La continuation (`ask`) reprend ensuite via `history`.
    const restore = useCallback((turns: AssistantTurn[]) => {
        abortRef.current?.abort()
        abortRef.current = null
        setMessages(turns)
        setError(null)
        setIsStreaming(false)
    }, [])

    const atLimit = messages.filter((m) => m.role === 'user').length >= MAX_ASSISTANT_TURNS

    return { messages, isStreaming, error, atLimit, ask, startNew, restore, reset }
}

export default useHelpAssistant
